import { Row } from 'react-table'
import { Account } from '@ally/data-types-afg-accounts'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import dayjs from 'dayjs'
import {
  autoPayExclusionChatCodes,
  oneTimePayExclusionChatCodes,
  noActiveVehiclesExclusionChatCodes,
  noActiveVehiclesExclusionProductSubTypes,
} from './constants'

export function isAutoAccountInGoodStanding(account: Account): boolean {
  return Boolean(
    account.meta &&
      !account.meta.accountPastDue &&
      !account.meta.autoPayEnrolled &&
      (!account.meta.exclusions || account.meta.exclusions.length === 0),
  )
}

export function isAutoPayVehicleAccount(account: Account): boolean {
  return Boolean(
    account.meta &&
      account.meta.autoPayEnrolled &&
      (!account.meta.exclusions || account.meta.exclusions.length === 0),
  )
}

export function isOneTimePayExclusionVehicleAccount(account: Account): boolean {
  return Boolean(
    account.meta &&
      !account.meta.autoPayEnrolled &&
      !account.meta.directPayEnrolled &&
      account.meta.liveChatAlertCodes?.some(code => {
        return oneTimePayExclusionChatCodes.includes(code)
      }),
  )
}

export function isAutoPayExclusionVehicleAccount(account: Account): boolean {
  return Boolean(
    account.meta &&
      account.meta.autoPayEnrolled &&
      !account.meta.directPayEnrolled &&
      account.meta.liveChatAlertCodes?.some(code => {
        return autoPayExclusionChatCodes.includes(code)
      }),
  )
}

export function isAutoAccountPastDue(account: Account): boolean {
  return Boolean(
    account.meta &&
      account.meta.accountPastDue &&
      (!account.meta.exclusions || account.meta.exclusions.length === 0),
  )
}

export function isAutoAccountWithNoActiveVehicle(account: Account): boolean {
  return Boolean(
    account.meta &&
      (account.meta.liveChatAlertCodes?.some(charCode =>
        noActiveVehiclesExclusionChatCodes.includes(charCode),
      ) ||
        (account.meta.productSubtype &&
          noActiveVehiclesExclusionProductSubTypes.includes(
            account.meta.productSubtype,
          ))),
  )
}

export function getAccountNickname(account: Account): string {
  return account.nickName || account.name
}

export function getMaskedAccountNumber(account: Account): string {
  return maskAccountNumber(account.accountNumber)
}

export function getReadableNextDueDate(account: Account): string {
  return account.meta?.nextPayment?.dueDate
    ? dayjs(account.meta.nextPayment.dueDate).format('MMM DD, YYYY')
    : ''
}

export function getReadablePastDueDate(account: Account): string {
  return account.meta?.pastDuePayment?.dueDate
    ? dayjs(account.meta.pastDuePayment.dueDate).format('MMM DD, YYYY')
    : ''
}

export function getReadableNextPaymentAmount(account: Account): string {
  return account.meta?.nextPayment?.totalAmountDue
    ? asCurrency(account.meta.nextPayment.totalAmountDue)
    : ''
}

export function getReadablePastDueAmount(account: Account): string {
  return account.meta?.pastDuePayment?.totalAmountDue
    ? asCurrency(account.meta.pastDuePayment.totalAmountDue)
    : ''
}

/**
 * Helper function for calculating row total
 * @param rows table rows
 * @param key key on the values object that resolves to a number
 * @returns string
 */
export function calculateRowTotal(rows: Row<Account>[], key: string): string {
  const rowsToSum = rows.filter(
    row => !isAutoAccountWithNoActiveVehicle(row.original),
  )
  if (rowsToSum.length > 0) {
    return asCurrency(rowsToSum.reduce((sum, row) => row.values[key] + sum, 0))
  }
  return 'Unavailable'
}

export const getAutoPayAccount = (accounts: Account[]): Account | undefined => {
  const autoPayAccounts = accounts.filter(acct => isAutoPayVehicleAccount(acct))

  if (autoPayAccounts.length === 0) return undefined

  if (autoPayAccounts.length === 1) return autoPayAccounts[0]

  const sortedAccounts = autoPayAccounts.sort((a, b) => {
    const dateA = getReadableNextDueDate(a)
    const dateB = getReadableNextDueDate(b)
    if (dayjs(dateA).diff(dayjs(dateB)) < 0) return -1
    if (dayjs(dateA).diff(dayjs(dateB)) > 0) return 1
    return 0
  })

  return sortedAccounts[0]
}

export const getPastDueAccount = (accounts: Account[]): Account | undefined => {
  const pastDueAccounts = accounts.filter(acct => isAutoAccountPastDue(acct))

  if (pastDueAccounts.length === 0) return undefined

  if (pastDueAccounts.length === 1) return pastDueAccounts[0]

  const sortedAccounts = pastDueAccounts.sort((a, b) => {
    const dateA = getReadablePastDueDate(a)
    const dateB = getReadablePastDueDate(b)
    if (dayjs(dateA).diff(dayjs(dateB)) < 0) return -1
    if (dayjs(dateA).diff(dayjs(dateB)) > 0) return 1
    return 0
  })

  return sortedAccounts[0]
}
